import type { PlayerProgress, FactionId, Subject } from '../shared/types';
import { masteryPercent } from '../shared/student-kp';
import { generateMul2x1 } from './generators/math/mul2x1';
import { loadChineseZhuyinBank, sampleBank } from './content/chinese-loader';
import { FACTIONS } from '../shared/factions';
import { loadProgress, saveProgress, addFactionXP } from '../shared/progress';
import { LessonController } from './lesson';
import { QuizOverlay } from './quiz/overlay';
import { showXpPopup, flashCard } from './quiz/feedback';
import { mountEscapeButton } from './escape-button';
import { getUiSoundPlayer } from './ui-sounds';
import { isProMode } from './settings';
import type { GeneratedQuestion } from './generators/math/types';
import type { LessonPhases } from './lesson';

const MASTERY_THRESHOLD = 60;
const MIN_ATTEMPTS = 3;

const SUBJECT_TO_FACTION: Record<Subject, FactionId> = {
  math: 'celestial', chinese: 'runic', english: 'techno', nature: 'elemental', social: 'dominion',
};

export interface RemediationMission {
  /** Game-world title for the kid — NEVER uses 弱點/補強/錯題 */
  missionTitle: string;
  /** Which faction (for emoji + color) */
  faction: FactionId;
  /** Number of KPs being targeted (parent dashboard might show) */
  weakKpCount: number;
  /** Built lesson phases ready for LessonController */
  phases: LessonPhases | null;  // null if nothing weak
}

export async function buildRemediationMission(progress: PlayerProgress): Promise<RemediationMission | null> {
  const weak = (progress.studentKPs ?? [])
    .filter(kp => masteryPercent(kp) < MASTERY_THRESHOLD && kp.totalAttempts >= MIN_ATTEMPTS)
    .sort((a, b) => masteryPercent(a) - masteryPercent(b));
  if (weak.length === 0) return null;

  // Group by faction; pick the faction with the most weak KPs to theme the mission
  const factionCounts: Partial<Record<FactionId, number>> = {};
  for (const kp of weak) {
    const f = SUBJECT_TO_FACTION[kp.subject];
    factionCounts[f] = (factionCounts[f] ?? 0) + 1;
  }
  const themedFaction = Object.entries(factionCounts)
    .sort((a, b) => (b[1] as number) - (a[1] as number))[0][0] as FactionId;
  const faction = FACTIONS[themedFaction];

  // Sample 3 weak KPs (or fewer if less available) × ~3 questions each = ~9 questions
  const topWeak = weak.slice(0, 3);
  const allQuestions: GeneratedQuestion[] = [];
  for (const kp of topWeak) {
    if (kp.subject === 'math') {
      // Use parametric generator for math KPs
      for (let i = 0; i < 3; i++) {
        allQuestions.push(generateMul2x1(Date.now() + i * 7, 1));
      }
    } else if (kp.subject === 'chinese') {
      const bank = await loadChineseZhuyinBank();
      const sampled = sampleBank(bank, 3);
      allQuestions.push(...sampled);
    }
    // Other subjects fall back silently in M5; M6 will add their generators
  }

  if (allQuestions.length === 0) return null;

  const phases: LessonPhases = {
    warmup: allQuestions.slice(0, 2),
    guide: allQuestions.slice(2, 5),
    challenge: allQuestions.slice(5),
  };

  return {
    missionTitle: `${faction.emoji} ${faction.displayName}有任務未完成,指揮官需要回援!`,
    faction: themedFaction,
    weakKpCount: weak.length,
    phases,
  };
}

export function runRemediationLesson(
  container: HTMLElement,
  mission: RemediationMission,
  onDone: () => void,
): void {
  if (!mission.phases) { onDone(); return; }
  const lesson = new LessonController(mission.phases, 1);
  const faction = FACTIONS[mission.faction];
  // v24 教學專業模式 — boss directive 2026-05-22: 教學要專業.
  const pro = isProMode();

  let overlay: QuizOverlay | null = new QuizOverlay({
    container,
    proMode: pro,
    onAnswer: (answer) => {
      const card = container.querySelector('.card') as HTMLElement | null;
      const result = lesson.submitAnswer(answer);
      if (!pro && card) flashCard(card, result.correct ? 'correct' : 'wrong');
      if (result.correct) {
        if (!pro) getUiSoundPlayer().play('answer-correct', 0.5);
      } else {
        if (!pro) getUiSoundPlayer().play('answer-wrong', 0.4);
      }
      if (!pro) {
        if (result.xp > 0) {
          getUiSoundPlayer().play('xp-popup', 0.35);
          showXpPopup(`+${Math.round(result.xp)} ${faction.emoji}`, '#ffd54f');
        } else if (!result.correct) {
          showXpPopup('再來!', '#80cbc4');
        }
      }
      setTimeout(() => {
        if (lesson.state === 'done') {
          unmountEscape();
          overlay?.destroy();
          overlay = null;
          const progress = loadProgress();
          if (progress) {
            addFactionXP(progress, mission.faction, Math.round(lesson.totalXP));
            saveProgress(progress);
          }
          onDone();
        } else {
          overlay?.render(lesson.currentQuestion(), lesson.state, lesson.positionInPhase());
        }
      }, result.correct ? 500 : 900);
    },
  });

  const unmountEscape = mountEscapeButton(() => {
    overlay?.destroy();
    overlay = null;
    unmountEscape();
    onDone();
  });

  overlay.render(lesson.currentQuestion(), lesson.state, lesson.positionInPhase());
}
